import React, { useEffect, useState } from "react";
import {
  X,
  CheckCircle2,
  Circle,
  Zap,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * MissionBrief — 5-step onboarding panel that shows above the dashboard
 * Overview until the user has claimed all 5 steps. When all are claimed,
 * the panel disappears entirely.
 *
 * Props:
 *   onJumpTab(tab_id)    — parent passes a handler that switches dashboard tabs
 *   onChange()            — called whenever state refreshes, so parent can re-fetch user
 */
const MissionBrief = ({ onJumpTab, onChange }) => {
  const [state, setState] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/state");
      setState(data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-claim any newly-completed unclaimed steps the moment we see them
  useEffect(() => {
    if (!state?.steps) return;
    const pending = state.steps.find((s) => s.done && !s.claimed);
    if (pending && !claiming) {
      claim(pending.id, /*silent*/ true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const claim = async (step_id, silent = false) => {
    setClaiming(step_id);
    try {
      const { data } = await api.post("/onboarding/claim", { step_id });
      if (!silent && data.tokens_awarded > 0) {
        toast.success(`+${data.tokens_awarded} Tam tokens unlocked!`);
      }
      await load();
      if (onChange) onChange();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Couldn't claim.");
    } finally {
      setClaiming(null);
    }
  };

  if (!state) return null;
  if (state.all_complete) return null; // Disappears entirely

  const completed = state.steps.filter((s) => s.claimed).length;
  const total = state.steps.length;
  const pct = Math.round((completed / total) * 100);

  return (
    <div
      data-testid="mission-brief"
      className="p-6 bg-[#0A192F] border border-cyan-400/30 rounded-sm cf-glow relative overflow-hidden"
    >
      {/* Diagonal accent line */}
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-cyan-400/5 rotate-45" />

      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-cyan-300" />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
                Earn Tam tokens
              </p>
            </div>
            <h3 className="mt-2 font-heading text-2xl font-black text-white tracking-tighter">
              Let&apos;s build your empire.
            </h3>
            <p className="mt-1 text-slate-400 text-sm">
              Knock out all {total} missions to unlock{" "}
              <span className="text-cyan-300 font-bold">
                {state.total_remaining.toLocaleString()} ⚡
              </span>{" "}
              tokens.
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Progress
            </p>
            <p className="mt-1 font-heading text-2xl font-black text-cyan-300">
              {completed} / {total}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1 bg-white/5 overflow-hidden rounded-sm">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Steps */}
        <div className="mt-5 space-y-2">
          {state.steps.map((s, i) => (
            <MissionStep
              key={s.id}
              index={i + 1}
              step={s}
              claiming={claiming === s.id}
              onClaim={() => claim(s.id)}
              onJump={() => onJumpTab && onJumpTab(s.tab)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const MissionStep = ({ index, step, claiming, onClaim, onJump }) => {
  const isClaimed = step.claimed;
  const isDone = step.done;
  return (
    <div
      data-testid={`mission-step-${step.id}`}
      className={`flex items-center gap-3 px-3 py-2.5 border rounded-sm transition-colors ${
        isClaimed
          ? "border-emerald-400/20 bg-emerald-400/5"
          : isDone
          ? "border-cyan-400/40 bg-cyan-400/5"
          : "border-white/5 hover:border-white/15 bg-[#050B14]"
      }`}
    >
      <span
        className={`shrink-0 w-7 h-7 flex items-center justify-center font-mono text-[11px] font-bold border ${
          isClaimed
            ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
            : isDone
            ? "border-cyan-400 text-cyan-200 bg-cyan-400/15"
            : "border-white/15 text-slate-400"
        }`}
      >
        {index}
      </span>

      {isClaimed ? (
        <CheckCircle2 size={14} className="text-emerald-300 shrink-0" />
      ) : (
        <Circle size={14} className={isDone ? "text-cyan-300 shrink-0" : "text-slate-600 shrink-0"} />
      )}

      <div className="flex-1 min-w-0">
        <p className={`text-sm ${isClaimed ? "text-slate-300 line-through" : "text-white"} truncate`}>
          {step.label}
        </p>
      </div>

      <span
        className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] font-bold tracking-widest border ${
          isClaimed
            ? "border-emerald-400/30 text-emerald-300 bg-emerald-400/10"
            : "border-cyan-400/30 text-cyan-300 bg-cyan-400/10"
        }`}
      >
        <Zap size={9} />+{step.reward}
      </span>

      {!isClaimed && (
        <button
          onClick={isDone ? onClaim : onJump}
          disabled={claiming}
          data-testid={`mission-action-${step.id}`}
          className={`shrink-0 px-3 py-1.5 font-bold text-[10px] uppercase tracking-widest rounded-sm flex items-center gap-1 ${
            isDone
              ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF]"
              : "border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300"
          }`}
        >
          {isDone ? (claiming ? "…" : "Claim") : (
            <>
              {step.cta} <ChevronRight size={11} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

/**
 * ConnectChannelsModal — pops on first dashboard visit only.
 * Dismissible. Once dismissed, the backend stores a flag so we don't
 * show it again. Tapping a channel jumps to the Connections tab.
 */
export const ConnectChannelsModal = ({ onClose, onConnectChannels }) => {
  const [closing, setClosing] = useState(false);

  const dismiss = async () => {
    if (closing) return;
    setClosing(true);
    try {
      await api.post("/onboarding/dismiss", { target: "connect_modal" });
    } catch {}
    onClose && onClose();
  };

  const channels = [
    { id: "instagram", label: "Instagram", available: true },
    { id: "facebook", label: "Facebook", available: true },
    { id: "x", label: "X (Twitter)", available: false },
    { id: "linkedin", label: "LinkedIn", available: false },
    { id: "tiktok", label: "TikTok", available: false },
    { id: "threads", label: "Threads", available: false },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      onClick={dismiss}
      data-testid="connect-channels-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm relative"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-white rounded-sm"
          data-testid="connect-modal-close"
        >
          <X size={16} />
        </button>

        <div className="p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
            Step 1 · Connect channels
          </p>
          <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-black tracking-tighter text-white">
            Right then. Where d&apos;ye run your empire?
          </h2>
          <p className="mt-2 text-slate-400 text-sm">
            Pick a channel and we&apos;ll wire it in. You can connect more later from the
            Connections tab.
          </p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {channels.map((c) => (
              <button
                key={c.id}
                onClick={() => c.available && onConnectChannels && onConnectChannels(c.id)}
                disabled={!c.available}
                data-testid={`connect-modal-channel-${c.id}`}
                className={`p-4 text-left border rounded-sm transition-colors ${
                  c.available
                    ? "border-cyan-400/30 hover:border-cyan-400 bg-[#050B14] hover:bg-cyan-400/5 cursor-pointer"
                    : "border-white/5 bg-[#050B14]/50 opacity-50 cursor-not-allowed"
                }`}
              >
                <p className="font-heading text-base font-bold text-white">{c.label}</p>
                <p
                  className={`mt-1 font-mono text-[9px] uppercase tracking-widest ${
                    c.available ? "text-cyan-300" : "text-slate-500"
                  }`}
                >
                  {c.available ? "Live now" : "Coming soon"}
                </p>
              </button>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
              No card required · 14-day free trial
            </p>
            <button
              onClick={dismiss}
              data-testid="connect-modal-skip"
              className="px-4 py-2 text-slate-300 hover:text-white text-xs font-mono uppercase tracking-widest"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionBrief;
